import { useEffect, useRef, useState } from "react";
import { forceSimulation, forceCenter, forceCollide, forceRadial } from "d3-force";
import { zoom as d3Zoom, zoomIdentity as d3ZoomIdentity } from 'd3-zoom';
import { select as d3Select } from 'd3-selection';
import { useLocation } from 'react-router-dom';
// import { mockTerrainsWithRandomRadii as baseTerrains } from "./data";
import { computeMinOuterRadius, getGradientIdForRadiusGlobal } from "./utilities";

export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export const useDraggableSheet = ({ onClose, threshold = 80, animationDuration = 300, lockBodyScroll = true }) => {
    const [dragOffset, setDragOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    const startYRef = useRef(null);

    useEffect(() => {
        if (!lockBodyScroll) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow || "";
        };
    }, [lockBodyScroll]);

    // Escape key dismissal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose?.();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleClose = () => {
        setDragOffset(window.innerHeight);
        setTimeout(() => {
            onClose?.();
            setDragOffset(0);
        }, animationDuration);
    };

    const startDrag = (clientY) => {
        setDragging(true);
        startYRef.current = clientY;
    };

    const moveDrag = (clientY) => {
        if (!dragging || startYRef.current === null) return;
        const diff = clientY - startYRef.current;
        if (diff > 0) setDragOffset(diff);
    };

    const endDrag = () => {
        setDragging(false);
        if (dragOffset > threshold) {
            handleClose();
        } else {
            setDragOffset(0);
        }
    };

    const touchHandlers = {
        onTouchStart: (e) => startDrag(e.touches[0].clientY),
        onTouchMove: (e) => {
            // e.preventDefault();
            moveDrag(e.touches[0].clientY);
        },
        onTouchEnd: endDrag,
    };

    const mouseHandlers = {
        onMouseDown: (e) => {
            startDrag(e.clientY);
            const onMouseMove = (e) => moveDrag(e.clientY);
            const onMouseUp = () => {
                endDrag();
                window.removeEventListener("mousemove", onMouseMove);
                window.removeEventListener("mouseup", onMouseUp);
            };
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        },
    };

    return {
        dragOffset,
        dragging,
        sheetStyle: {
            transform: `translateY(${dragOffset}px)`,
            transition: dragging ? "none" : `transform ${animationDuration}ms ease`,
        },
        overlayHandlers: { ...touchHandlers, ...mouseHandlers, onClick: handleClose },
    };
};

export function useViewport() {
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setSize({
                width: window.innerWidth,
                height: window.innerHeight - 380
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return size;
}

export function usePreventZoom() {
    useEffect(() => {
        const preventZoom = (e) => {
            // Prevent zoom with Ctrl/Cmd + Mouse Wheel
            if ((e.ctrlKey || e.metaKey) && (e.deltaY || e.deltaX)) {
                e.preventDefault();
            }
            // Prevent zoom with Ctrl/Cmd + '+'/'-'
            if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
                e.preventDefault();
            }
        };

        window.addEventListener('wheel', preventZoom, { passive: false });
        window.addEventListener('keydown', preventZoom, { passive: false });

        return () => {
            window.removeEventListener('wheel', preventZoom, { passive: false });
            window.removeEventListener('keydown', preventZoom, { passive: false });
        };
    }, []);
}

export function useForceSimulation({ width = 1000, height = 1000, heatmap }) {
    const [nodes, setNodes] = useState([]);
    const dataRef = useRef([]);
    const simulationRef = useRef();



    useEffect(() => {
        const mockTerrains = heatmap.map(t => ({
            ...t,
            computedRadius: computeMinOuterRadius(t.tags)
        }));
        const data = mockTerrains.map((terrain, i) => ({
            id: i,
            terrain: terrain.terrain,
            fontSize: terrain.fontSize,
            tags: terrain.tags,
            r: terrain.computedRadius
        }));
        dataRef.current = data;

        const simulation = forceSimulation(data)
            .force('center', forceCenter(width / 20, height / 20))
            .force('collide', forceCollide()
                .radius(d => d.r * 0.4)
                .strength(0.2)
                .iterations(1)
            )
            .alphaDecay(0.9)
            .velocityDecay(0.4);

        simulationRef.current = simulation;

        // Only update state when simulation is nearly stable
        function onEnd() {
            setNodes([...dataRef.current]);
        }

        simulation.on('end', onEnd);

        // Optionally, provide a preview after a short delay
        const previewTimeout = setTimeout(() => {
            setNodes([...dataRef.current]);
        }, 300);

        return () => {
            simulation.stop();
            simulation.on('end', null);
            clearTimeout(previewTimeout);
        };
    }, [width, height, heatmap]);

    return nodes;
}

export function useZoomDetection({
    svgRef,
    gRef,
    containerRef,
    nodes,
    width,
    height,
    zoomThreshold = 1.5,
    minZoom = 0.2,
    maxZoom = 5
}) {
    const [transform, setTransform] = useState({ k: 1, x: width / 2, y: height / 4 });
    const [zoomedNode, setZoomedNode] = useState(null);
    const [zoomCenter, setZoomCenter] = useState(null);
    const zoomRef = useRef(null);

    useEffect(() => {
        if (!svgRef.current || !gRef.current) return;

        const zoom = d3Zoom()
            .scaleExtent([minZoom, maxZoom])
            .on('zoom', (event) => {
                const { k, x, y } = event.transform;
                setTransform({ k, x, y });

                // Only check when zoomed in
                if (k > zoomThreshold && nodes.length > 0) {
                    // Calculate the SVG coordinates of the viewport center
                    const viewportCenter = {
                        x: (containerRef.current.scrollLeft + width / 2 - x) / k,
                        y: (containerRef.current.scrollTop + height / 2 - y) / k,
                    };
                    setZoomCenter(viewportCenter);

                    // Find the closest node
                    let minDist = Infinity;
                    let closest = null;
                    for (const node of nodes) {
                        const dist = Math.hypot(node.x - viewportCenter.x, node.y - viewportCenter.y);
                        if (dist < minDist) {
                            minDist = dist;
                            closest = node;
                        }
                    }
                    setZoomedNode(closest ? closest.terrain : null);
                } else {
                    setZoomedNode(null);
                    setZoomCenter(null);
                }
            });

        const svg = d3Select(svgRef.current);
        svg.call(zoom);

        // Initialize the zoom with the centered position
        const initialTransform = d3ZoomIdentity.translate(width / 2, height / 4);
        svg.call(zoom.transform, initialTransform);

        zoomRef.current = zoom;

        // Cleanup function
        return () => {
            if (svgRef.current) {
                svg.on('.zoom', null); // Remove all zoom event listeners
                zoomRef.current = null;
            }
        };
    }, [svgRef, gRef, containerRef, nodes, width, height, zoomThreshold]);

    const resetZoom = () => {
        if (zoomRef.current && svgRef.current) {
            const resetTransform = d3ZoomIdentity.translate(width / 2, height / 2);
            zoomRef.current.transform(
                d3Select(svgRef.current),
                resetTransform
            );
            setTransform({ k: 1, x: width / 2, y: height / 4 });
            setZoomedNode(null);
            setZoomCenter(null);
        }
    };

    return { transform, zoomedNode, zoomCenter, resetZoom };
}

export function useTagForceSimulation({ tags, r }) {
    const [tagNodes, setTagNodes] = useState([]);
    const [connections, setConnections] = useState([]);

    useEffect(() => {
        // Create nodes from tags
        const data = tags.map(tag => ({
            ...tag,
            x: 0,
            y: 0,
            gradientId: getGradientIdForRadiusGlobal(tag.radius, tags)
        }));

        // Find min and max radius for normalization
        const minRadius = Math.min(...data.map(d => d.radius));
        const maxRadius = Math.max(...data.map(d => d.radius));
        const radiusRange = maxRadius - minRadius;

        const simulation = forceSimulation(data)
            .force('center', forceCenter(0, 0))
            .force('collide', forceCollide()
                .radius(d => d.radius + 15)
                .strength(1)
                .iterations(5)
            )
            .force('radial', forceRadial(
                d => {
                    // Normalize radius to 0-1 range
                    const normalizedRadius = (d.radius - minRadius) / radiusRange;
                    // Ensure minimum distance of 0.3r from center, with bigger nodes closer (0.3 to 0.8 of bubble radius)
                    return r * (0.5 + (1 * (1.5 - normalizedRadius)));
                },
                0, 0
            ).strength(0.9))
            .alphaDecay(0.02)
            .velocityDecay(0.4);

        simulation.on('tick', () => {
            // Keep tags within the bubble
            data.forEach(d => {
                const dist = Math.hypot(d.x, d.y);
                if (dist > r - d.radius) {
                    const scale = (r - d.radius) / dist;
                    d.x *= scale;
                    d.y *= scale;
                }
            });
            setTagNodes([...data]);

            // Update connections based on current positions
            const newConnections = [];

            // Sort nodes by radius (biggest to smallest)
            const sortedNodes = [...data].sort((a, b) => b.radius - a.radius);

            // Take top 30% of nodes as "big" nodes
            const bigNodeCount = Math.max(1, Math.ceil(sortedNodes.length * 0.3));
            const bigNodes = sortedNodes.slice(0, bigNodeCount);
            const smallNodes = sortedNodes.slice(bigNodeCount);

            // Connect only big nodes to center
            bigNodes.forEach(node => {
                newConnections.push({
                    from: { x: 0, y: 0 },
                    to: { x: node.x, y: node.y }
                });
            });

            // Connect small nodes to their closest big node
            smallNodes.forEach(smallNode => {
                let closestBigNode = bigNodes[0];
                let minDist = Math.hypot(smallNode.x - bigNodes[0].x, smallNode.y - bigNodes[0].y);

                // Find closest big node
                for (let i = 1; i < bigNodes.length; i++) {
                    const bigNode = bigNodes[i];
                    const dist = Math.hypot(smallNode.x - bigNode.x, smallNode.y - bigNode.y);
                    if (dist < minDist) {
                        minDist = dist;
                        closestBigNode = bigNode;
                    }
                }

                // Connect to closest big node
                newConnections.push({
                    from: { x: closestBigNode.x, y: closestBigNode.y },
                    to: { x: smallNode.x, y: smallNode.y }
                });
            });

            setConnections(newConnections);
        });

        return () => simulation.stop();
    }, [tags, r]);

    return { tagNodes, connections };
}

export const usePathLocation = () => {
    const [currentLocation, setCurrentLocation] = useState(null);
    const [previousLocation, setPreviousLocation] = useState(null);
    const location = useLocation();

    useEffect(() => {
        // Check 
        if (currentLocation) {
            setPreviousLocation(currentLocation);
        }
        setCurrentLocation(location.pathname);
        // Store in sessionStorage
        sessionStorage.setItem('previousLocation', location.pathname);
        sessionStorage.setItem('currentLocation', location.pathname);
    }, [location]);

    return { currentLocation, previousLocation };
};

export const usePreviousLocation = () => {
    const [previousLocation, setPreviousLocation] = useState(null);

    useEffect(() => {
        // Get previous location from sessionStorage
        const storedPreviousLocation = sessionStorage.getItem('previousLocation');
        if (storedPreviousLocation) {
            setPreviousLocation(storedPreviousLocation);
        }
    }, [location]);
    return previousLocation;
};

export const useKeyboardDetection = () => {
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const [availableHeight, setAvailableHeight] = useState(window.innerHeight);
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    useEffect(() => {
        let initialHeight = window.innerHeight;
        let currentHeight = window.innerHeight;

        const handleResize = () => {
            currentHeight = window.innerHeight;
            
            // Calculate keyboard height (difference between initial and current height)
            const newKeyboardHeight = Math.max(0, initialHeight - currentHeight);
            
            // Consider keyboard visible if height difference is significant (>150px)
            const isKeyboardVisible = newKeyboardHeight > 150;
            
            setKeyboardVisible(isKeyboardVisible);
            setKeyboardHeight(newKeyboardHeight);
            setAvailableHeight(currentHeight);
            
            // Apply styles directly to root element
            applyRootStyles(isKeyboardVisible, currentHeight + 150);
        };

        const handleVisualViewportChange = () => {
            if (window.visualViewport) {
                const viewportHeight = window.visualViewport.height;
                const newKeyboardHeight = Math.max(0, initialHeight - viewportHeight);
                const isKeyboardVisible = newKeyboardHeight > 150;
                
                setKeyboardVisible(isKeyboardVisible);
                setKeyboardHeight(newKeyboardHeight);
                setAvailableHeight(viewportHeight);
                
                // Apply styles directly to root element
                applyRootStyles(isKeyboardVisible, viewportHeight + 150);
            }
        };

        // Function to apply styles directly to root element
        const applyRootStyles = (isVisible, height) => {
            const rootElement = document.getElementById('root');
            if (rootElement) {
                if (isVisible) {
                    // rootElement.style.maxHeight = `${height}px`;
                    console.log(height)
                    rootElement.style.height = `${height}px`;
                    rootElement.style.overflow = 'auto';
                    rootElement.classList.add('keyboard-open');
                } else {
                    // rootElement.style.maxHeight = '100dvh';
                    rootElement.style.height = '100dvh';
                    rootElement.style.overflow = 'visible';
                    rootElement.classList.remove('keyboard-open');
                }
            }
        };

        // Listen for resize events (works on most mobile browsers)
        window.addEventListener('resize', handleResize);
        
        // Listen for visual viewport changes (more accurate on modern browsers)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleVisualViewportChange);
        }

        // Set initial values
        setAvailableHeight(initialHeight);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
            }
            
            // Reset root styles on cleanup
            const rootElement = document.getElementById('root');
            if (rootElement) {
                // rootElement.style.maxHeight = '';
                rootElement.style.height = '';
                rootElement.style.overflow = '';
                rootElement.classList.remove('keyboard-open');
            }
        };
    }, []);

    // Helper function to get CSS value for max-height
    const getMaxHeightCSS = (fallbackHeight = '100dvh') => {
        if (keyboardVisible && availableHeight > 0) {
            return `${availableHeight}px`;
        }
        return fallbackHeight;
    };

    // Helper function to get CSS value for height
    const getHeightCSS = (fallbackHeight = '100dvh') => {
        if (keyboardVisible && availableHeight > 0) {
            return `${availableHeight}px`;
        }
        return fallbackHeight;
    };

    return {
        keyboardVisible,
        availableHeight,
        keyboardHeight,
        getMaxHeightCSS,
        getHeightCSS
    };
};


export const useKeyboardDetectionNoRoot = () => {
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const [availableHeight, setAvailableHeight] = useState(window.innerHeight);
    const [keyboardHeight, setKeyboardHeight] = useState(0);

    useEffect(() => {
        let initialHeight = window.innerHeight;
        let currentHeight = window.innerHeight;

        const handleResize = () => {
            currentHeight = window.innerHeight;

            // Calculate keyboard height (difference between initial and current height)
            const newKeyboardHeight = Math.max(0, initialHeight - currentHeight);

            // Consider keyboard visible if height difference is significant (>150px)
            const isKeyboardVisible = newKeyboardHeight > 150;

            setKeyboardVisible(isKeyboardVisible);
            setKeyboardHeight(newKeyboardHeight);
            setAvailableHeight(currentHeight);

            // Apply styles directly to root element
        };

        const handleVisualViewportChange = () => {
            if (window.visualViewport) {
                const viewportHeight = window.visualViewport.height;
                const newKeyboardHeight = Math.max(0, initialHeight - viewportHeight);
                const isKeyboardVisible = newKeyboardHeight > 150;

                setKeyboardVisible(isKeyboardVisible);
                setKeyboardHeight(newKeyboardHeight);
                setAvailableHeight(viewportHeight);
            }
        };

        // Listen for resize events (works on most mobile browsers)
        window.addEventListener('resize', handleResize);

        // Listen for visual viewport changes (more accurate on modern browsers)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleVisualViewportChange);
        }

        // Set initial values
        setAvailableHeight(initialHeight);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
            }
        };
    }, []);

    // Helper function to get CSS value for max-height
    const getMaxHeightCSS = (fallbackHeight = '100dvh') => {
        if (keyboardVisible && availableHeight > 0) {
            return `${availableHeight}px`;
        }
        return fallbackHeight;
    };

    // Helper function to get CSS value for height
    const getHeightCSS = (fallbackHeight = '100dvh') => {
        if (keyboardVisible && availableHeight > 0) {
            return `${availableHeight}px`;
        }
        return fallbackHeight;
    };

    return {
        keyboardVisible,
        availableHeight,
        keyboardHeight,
        getMaxHeightCSS,
        getHeightCSS
    };
};
